import { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { X, Download } from "lucide-react";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "install-prompt-dismissed-until";
const PROMPT_PATHS = new Set(["/dashboard", "/holdings", "/actions", "/review-cadence", "/discover"]);

export default function InstallPrompt() {
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOSInstallHint, setIsIOSInstallHint] = useState(false);
  const canShowOnPath = useMemo(() => PROMPT_PATHS.has(location.pathname), [location.pathname]);

  useEffect(() => {
    // Check if already installed (standalone mode)
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setIsInstalled(true);
      return;
    }

    const dismissedUntil = Number(localStorage.getItem(DISMISS_KEY) || "0");
    if (dismissedUntil && Date.now() < dismissedUntil) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (canShowOnPath) {
        setShowBanner(true);
      }
    };

    const installedHandler = () => {
      setIsInstalled(true);
      setShowBanner(false);
      localStorage.removeItem(DISMISS_KEY);
    };

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', installedHandler);

    // For iOS Safari — show manual instructions
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isIOS && isSafari && !window.navigator.standalone) {
      setIsIOSInstallHint(true);
      if (canShowOnPath) {
        setTimeout(() => setShowBanner(true), 2500);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, [canShowOnPath]);

  useEffect(() => {
    if (!canShowOnPath || isInstalled) {
      setShowBanner(false);
      return;
    }
    const dismissedUntil = Number(localStorage.getItem(DISMISS_KEY) || "0");
    if (dismissedUntil && Date.now() < dismissedUntil) return;
    if (deferredPrompt || isIOSInstallHint) {
      setShowBanner(true);
    }
  }, [canShowOnPath, deferredPrompt, isIOSInstallHint, isInstalled]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    }
  };

  const dismiss = () => {
    setShowBanner(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now() + 7 * 24 * 60 * 60 * 1000));
  };

  if (!showBanner || isInstalled || !canShowOnPath) return null;

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  return (
    <div
      data-testid="install-prompt"
      className="fixed left-4 right-4 z-[60] mx-auto max-w-sm animate-in slide-in-from-bottom-4 duration-300"
      style={{ bottom: "calc(5.5rem + env(safe-area-inset-bottom, 0px))" }}
    >
      <div className="bg-card border border-primary/30 rounded-xl p-4 shadow-lg shadow-primary/10 backdrop-blur-sm">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 bg-primary/10 rounded-lg p-2">
            <Download className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-foreground">Install Investing OS</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {isIOS
                ? 'Save it to your Home Screen for a faster, full-screen workflow.'
                : 'Get the full app experience on your phone without browser chrome.'}
            </p>
          </div>
          <button
            data-testid="install-dismiss-btn"
            onClick={dismiss}
            className="text-muted-foreground hover:text-foreground p-1"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {isIOS && (
          <div className="mt-3 rounded-lg bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground leading-5">
            iPhone Safari: tap <span className="font-medium text-foreground">Share</span>, then <span className="font-medium text-foreground">Add to Home Screen</span>.
          </div>
        )}
        {deferredPrompt && (
          <Button
            data-testid="install-confirm-btn"
            onClick={handleInstall}
            size="sm"
            className="w-full mt-3 gap-2"
          >
            <Download className="h-4 w-4" /> Install App
          </Button>
        )}
      </div>
    </div>
  );
}
